import { Button } from "@jammable/ui-core";
import React, { useState, useEffect, useRef } from "react";
import ModelConvertSettings from "./ModelConvertSettings";
import { useFormik } from "formik";
import * as yup from "yup";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import ReactCountryFlag from "react-country-flag";
import { FaChevronDown } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { TextArea } from "@jammable/ui-components/Forms";
import Dropdown from "../dropdown/Dropdown";
import CreatorBadge from "../CreatorBadge";
import useSubscription from "@/hooks/useSubscription";

const CreateTTSFlow = ({ model, setIsOpen, setUpgradeModalOpen, ip }) => {
  const [hasText, setHasText] = useState(false);
  const [voices, setVoices] = useState([{ ...model, pitchScaling: 0 }]);
  const [expression, setExpression] = useState({
    displayName: "Default",
    emoji: "😶",
  });

  const [accent, setAccent] = useState({
    displayName: "American",
    flagCode: "US",
  });
  const [speed, setSpeed] = useState({ displayName: "Default", emoji: "🎯" });

  const validationSchema = yup.object().shape({
    text: yup
      .string()
      .required("Text is required")
      .max(2500, "Maximum 2500 characters allowed!"),
  });

  const formik = useFormik({
    initialValues: {
      text: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (formik.isValid) {
        setHasText(true);
      } else {
        if (formik.errors.text) {
          toast.error(formik.errors.text);
        }
      }
    },
  });

  // ... existing code ...

  const genders = [
    { displayName: "Female", emoji: "👩", value: "female" },

    { displayName: "Male", emoji: "👨", value: "male" },
  ];
  const [gender, setGender] = useState(
    model?.tts?.gender === "female"
      ? { displayName: "Female", emoji: "👩", value: "female" }
      : { displayName: "Male", emoji: "👨", value: "male" },
  );

  const accents = [
    { displayName: "American", flagCode: "US" },
    { displayName: "British", flagCode: "GB" },
    { displayName: "French", flagCode: "FR" },
    { displayName: "German", flagCode: "DE" },
    { displayName: "Spanish", flagCode: "ES" },
    { displayName: "Italian", flagCode: "IT" },
    { displayName: "Korean", flagCode: "KR" },
    { displayName: "Australian", flagCode: "AU" },
    { displayName: "Japanese", flagCode: "JP" },
  ];

  const expressions = [
    { displayName: "Default", emoji: "😶" },
    { displayName: "Whispered", emoji: "🤫" },
    { displayName: "Happy", emoji: "😁" },
    { displayName: "Sad", emoji: "😢" },
    { displayName: "Angry", emoji: "🤬" },
    { displayName: "Shouting", emoji: "🤯" },
    { displayName: "Terrified", emoji: "😵" },
  ];

  const speeds = [
    { displayName: "Slowest", emoji: "🐌" },
    { displayName: "Slower", emoji: "🐢" },
    { displayName: "Default", emoji: "🎯" },
    { displayName: "Faster", emoji: "🏃" },
    { displayName: "Fastest", emoji: "🏎️" },
  ];

  const handleFormSubmit = (event) => {
    event.preventDefault();

    // Perform additional actions here
    if (formik.errors.text) {
      toast.error(formik.errors.text);
      return;
    }

    // Then call formik's submit function
    formik.handleSubmit();
  };

  const setPitchScalingForVoice = (voiceId, pitch) => {
    const updatedVoices = voices.map((voice) => {
      if (voice._id === voiceId) {
        return { ...voice, pitchScaling: pitch };
      }
      return voice;
    });
    setVoices(updatedVoices);
  };
  const { hasCreatorPlan, hasPowerPlan, hasCustomPlan } = useSubscription();

  const notCreator = !hasCreatorPlan() && !hasPowerPlan();

  return (
    <div
      className={`tw-w-full md:tw-w-[30rem] tw-h-max tw-w-full tw-flex tw-flex-col tw-gap-3 
      ${hasText ? "md:tw-w-[30rem]" : "md:tw-w-[30rem]"}
    `}
    >
      {hasText ? (
        <ModelConvertSettings
          finalUrl={null}
          progress={100}
          text={formik.values.text}
          mode="tts"
          ip={ip}
          voices={voices}
          toolMode={false}
          expression={expression?.displayName}
          accent={accent?.flagCode ?? "US"}
          speed={speed?.displayName}
          setIsOpen={setIsOpen}
          setUpgradeModalOpen={setUpgradeModalOpen}
          setPitchScalingForVoice={setPitchScalingForVoice}
          gender={gender?.value ?? "male"}
        />
      ) : (
        <>
          <div className="tw-text-xl tw-font-bold tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-1">
            Text To Speech
            {notCreator && <CreatorBadge />}
          </div>
          <form
            onSubmit={handleFormSubmit}
            className="tw-flex tw-flex-col tw-gap-3 tw-items-center"
          >
            <TextArea
              spellCheck="false"
              placeholder="Enter your text here..."
              className="tw-w-full tw-bg-gray-100"
              name="text"
              value={formik.values.text}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              aria-label="text"
              minRows={8}
              error={formik.touched.text && formik.errors.text}
            />
            <div className="tw-bg-gray-100 tw-normal-nums tw-p-2 tw-text-sm tw-gap-1 tw-opacity-60  tw-py-0 tw-gap-1dwq tw-rounded-xl tw-font-bold tw-flex">
              <span
                className={`tw-normal-nums ${formik.values.text.length > 2500 ? "tw-text-red-600" : "tw-opacity-50"} `}
              >
                {formik.values.text.length}
              </span>
              <div className="tw-h-100 tw-normal-nums tw-bg-gray-600 tw-rounded-full tw-w-[2px]" />

              <span>2500</span>
            </div>
            <div className="tw-flex tw-gap-2 tw-flex-col tw-w-full">
              <Dropdown
                // dropdownItems={
                //   <DropdownMenuItem className="tw-z-100 tw-flex tw-flex-col">
                //     {genders.map((genderOption) => (
                //       <div
                //         onClick={() => setGender(genderOption)}
                //         className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-purple-100 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                //         key={genderOption.displayName}
                //       >
                //         {genderOption.emoji} {genderOption.displayName}
                //       </div>
                //     ))}
                //   </DropdownMenuItem>
                // }
                dropdownItems={
                  <>
                    {genders.map((genderOption) => (
                      <DropdownMenuItem
                        key={genderOption?.displayName}
                        className="tw-z-100 tw-flex tw-flex-col"
                      >
                        <div
                          onClick={() => setGender(genderOption)}
                          className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-purple-100 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                        >
                          {genderOption.emoji} {genderOption.displayName}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                }
              >
                <div className="tw-w-full tw-cursor-pointer tw-items-center tw-text-left tw-align-left tw-flex tw-justify-between tw-border-2 tw-border-black/10 tw-border-solid tw-p-3 tw-px-4 tw-rounded-xl tw-font-bold">
                  Gender
                  <div className="tw-flex tw-items-center tw-gap-1.5 ">
                    {gender?.emoji}
                    <span className="tw-text-black/40">
                      {gender?.displayName}
                    </span>
                    <FaChevronDown className="tw-text-black/40" />
                  </div>
                </div>
              </Dropdown>
              <Dropdown
                dropdownItems={
                  <>
                    {accents.map((accent) => {
                      return (
                        <DropdownMenuItem
                          key={accent?.displayName}
                          className="tw-z-100 tw-flex tw-flex-col tw-w-full"
                        >
                          <div
                            onClick={() => {
                              setAccent(accent);
                            }}
                            className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-purple-100 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                          >
                            <ReactCountryFlag
                              className="emojiFlag"
                              countryCode={accent.flagCode}
                              aria-label="United States"
                              svg
                            />
                            {accent?.displayName}
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                }
              >
                <div className="tw-w-full tw-cursor-pointer tw-items-center tw-relative tw-text-left tw-align-left tw-flex tw-justify-between tw-border-2 tw-border-black/10 tw-border-solid tw-p-3 tw-px-4 tw-rounded-xl tw-font-bold">
                  Accent
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-black/40">
                    <ReactCountryFlag
                      className="emojiFlag"
                      countryCode={accent?.flagCode}
                      aria-label="United States"
                      svg
                    />
                    {accent?.displayName}
                    <FaChevronDown />
                  </div>
                </div>
              </Dropdown>
              <Dropdown
                dropdownItems={
                  <>
                    {expressions.map((expression) => {
                      return (
                        <DropdownMenuItem
                          key={expression.displayName}
                          className="tw-z-100 tw-flex tw-flex-col"
                        >
                          <div
                            onClick={() => {
                              setExpression(expression);
                            }}
                            className="tw-flex tw-items-center tw-rounded-lg tw-cursor-pointer hover:tw-bg-purple-100 tw-gap-2 tw-p-1.5 tw-px-2 tw-text-center tw-w-full tw-font-bold"
                          >
                            {expression.emoji} {expression.displayName}
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                }
              >
                <div className="tw-w-full tw-cursor-pointer tw-items-center tw-text-left tw-align-left tw-flex tw-justify-between tw-border-2 tw-border-black/10 tw-border-solid tw-p-3 tw-px-4 tw-rounded-xl tw-font-bold">
                  Expression
                  <div className="tw-flex tw-items-center tw-gap-1.5 ">
                    {expression?.emoji}
                    <span className="tw-text-black/40">
                      {expression?.displayName}
                    </span>
                    <FaChevronDown className="tw-text-black/40" />
                  </div>
                </div>
              </Dropdown>
            </div>
            <Button type="submit" className="tw-w-full tw-mt-2 tw-rounded-full">
              Continue
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default CreateTTSFlow;
